import React from 'react';
import appIcon from "/icons/icon-512.png";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState(prev => ({
      error,
      errorInfo,
      errorCount: prev.errorCount + 1,
    }));
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleClearData = async () => {
    try {
      localStorage.clear();
      const databases = await indexedDB.databases?.();
      if (databases) {
        for (const db of databases) {
          indexedDB.deleteDatabase(db.name);
        }
      }
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to clear data:', err);
      window.location.reload();
    }
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children } = this.props;

    if (!hasError) return children;

    const errorMessage = error?.message || 'Something went wrong';
    const isDev = import.meta.env.DEV;

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #14532d 0%, #1c1917 60%, #292524 100%)',
        padding: '2rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#fff',
      }}>
        <img
          src={appIcon}
          alt="Sheepheard"
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '22px',
            objectFit: 'contain',
            display: 'block',
            marginBottom: '1.5rem',
            opacity: 0.8,
          }}
        />

        <h1 style={{
          fontFamily: 'Georgia, serif',
          fontSize: '1.8rem',
          fontWeight: '800',
          margin: '0 0 0.5rem 0',
          letterSpacing: '-0.01em',
        }}>
          Oops!
        </h1>

        <p style={{
          fontSize: '0.9rem',
          color: 'rgba(255,255,255,0.8)',
          margin: '0 0 1.5rem 0',
          textAlign: 'center',
          maxWidth: '320px',
          lineHeight: '1.5',
        }}>
          We encountered an unexpected error. Don't worry — your data is safe.
        </p>

        {isDev && error && (
          <div style={{
            background: 'rgba(0,0,0,0.3)',
            borderLeft: '3px solid #ff6b6b',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1.5rem',
            maxWidth: '100%',
            maxHeight: '200px',
            overflow: 'auto',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: '#ffd9d9',
            textAlign: 'left',
            lineHeight: '1.4',
          }}>
            <strong>Error:</strong> {errorMessage}
            {errorInfo && (
              <pre style={{ margin: '0.5rem 0 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {errorInfo.componentStack.slice(0, 500)}
                {errorInfo.componentStack.length > 500 ? '...' : ''}
              </pre>
            )}
          </div>
        )}

        {!isDev && errorCount > 2 && (
          <p style={{
            fontSize: '0.85rem',
            color: '#ffb3b3',
            margin: '0 0 1.5rem 0',
            textAlign: 'center',
          }}>
            Multiple errors detected. You may need to clear app data.
          </p>
        )}

        <div style={{
          display: 'flex',
          gap: '0.75rem',
          flexWrap: 'wrap',
          justifyContent: 'center',
          marginTop: '1.5rem',
          maxWidth: '100%',
        }}>
          <button
            onClick={this.handleReset}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#166534',
              color: '#fff',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.target.style.background = '#1f7f3d'}
            onMouseLeave={e => e.target.style.background = '#166534'}
          >
            Try Again
          </button>

          <button
            onClick={this.handleReload}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '0.9rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => e.target.style.background = 'rgba(255,255,255,0.25)'}
            onMouseLeave={e => e.target.style.background = 'rgba(255,255,255,0.15)'}
          >
            Reload
          </button>

          {errorCount > 2 && (
            <button
              onClick={this.handleClearData}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(255,107,107,0.2)',
                color: '#ffb3b3',
                border: '1px solid rgba(255,107,107,0.4)',
                borderRadius: '0.5rem',
                fontWeight: '600',
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={e => {
                e.target.style.background = 'rgba(255,107,107,0.3)';
                e.target.style.color = '#ff9999';
              }}
              onMouseLeave={e => {
                e.target.style.background = 'rgba(255,107,107,0.2)';
                e.target.style.color = '#ffb3b3';
              }}
            >
              Clear Data & Reset
            </button>
          )}
        </div>

        <p style={{
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.4)',
          margin: '2rem 0 0 0',
          textAlign: 'center',
        }}>
          If the problem persists, please contact support.
        </p>
      </div>
    );
  }
}

export default ErrorBoundary;
